import React from 'react';
import moment from 'moment';
import get from 'lodash/get';
import { ERR_CONFIG_DATE_FORMAT } from '@utils/constants';
import ColumnFilterHeader from './ColumnFilterHeader';

const STATUS_STYLES: Record<number, { label: string; color: string }> = {
  0: { label: 'Deleted', color: '#8c8c8c' },
  1: { label: 'Disabled', color: '#cf1322' },
  2: { label: 'Enabled', color: '#389e0d' },
};

function ConfigStatusCell({ rowData }) {
  const { label, color } = STATUS_STYLES[rowData.state] ?? {
    label: '—',
    color: undefined,
  };
  return <span style={{ color, fontWeight: 500 }}>{label}</span>;
}

export function getSearchableCellText(rowData, dataKey: string): string {
  switch (dataKey) {
    case 'name':
      return String(rowData.name ?? '');
    case 'state': {
      const s = STATUS_STYLES[rowData.state];
      return s ? `${s.label} ${rowData.state}` : String(rowData.state ?? '');
    }
    case 'updatedAt':
      return moment(rowData.updatedAt).format(ERR_CONFIG_DATE_FORMAT);
    case 'rules':
      return String(rowData.rules ?? '');
    case 'updatedBy.email':
      return String(get(rowData, 'updatedBy.email') ?? '');
    default:
      return '';
  }
}

export function filterConfigTableData(rows: any[], filters: Record<string, string>): any[] {
  const active = Object.entries(filters).filter(([, v]) => v && String(v).trim());
  if (active.length === 0) {
    return rows;
  }
  return rows.filter((row) =>
    active.every(([key, raw]) => {
      const q = String(raw).trim().toLowerCase();
      return getSearchableCellText(row, key).toLowerCase().includes(q);
    }),
  );
}

function ConfigTableFilterHeader({
  column,
}: {
  column: { title: React.ReactNode; dataKey: string };
}) {
  return <ColumnFilterHeader column={column} />;
}
ConfigTableFilterHeader.displayName = 'ConfigTableFilterHeader';

function buildConfigTableColumns() {
  return [
    {
      title: 'Error Name',
      dataKey: 'name',
      key: 'name',
      width: 350,
      sortable: true,
      resizable: true,
      headerRenderer: ConfigTableFilterHeader,
    },
    {
      title: 'Updated on',
      dataKey: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      sortable: true,
      resizable: true,
      dataGetter: ({ rowData }) => moment(rowData.updatedAt).format(ERR_CONFIG_DATE_FORMAT),
      headerRenderer: ConfigTableFilterHeader,
    },
    {
      title: 'Rule',
      dataKey: 'rules',
      key: 'rules',
      width: 450,
      sortable: true,
      resizable: true,
      headerRenderer: ConfigTableFilterHeader,
    },
    {
      title: 'Status',
      dataKey: 'state',
      key: 'state',
      width: 130,
      sortable: true,
      resizable: true,
      cellRenderer: ({ rowData }) => <ConfigStatusCell rowData={rowData} />,
      headerRenderer: ConfigTableFilterHeader,
    },
    {
      title: 'Updated By',
      dataKey: 'updatedBy.email',
      key: 'updatedBy.email',
      width: 200,
      sortable: true,
      resizable: true,
      headerRenderer: ConfigTableFilterHeader,
    },
  ];
}

export const configTableDataColumns = buildConfigTableColumns();

export function ruleObject(rule) {
  const rules = rule.rules.split('OR').map((element) => element.trim());
  const ruleObj = rules.reduce((result, element, index) => {
    result[index] = {};
    result[index].operator = element.split(' ')[1];
    result[index].value = element.split(result[index].operator)[1].trim();
    return result;
  }, []);
  return ruleObj;
}

export const displayMessage = (state) => {
  let statement = '';
  switch (state) {
    case 0:
      statement = 'Once deleted all the business error will be marked as technical errors.';
      break;
    case 1:
      statement = 'Once disabled all the business error will be marked as technical errors.';
      break;
    case 2:
      statement =
        'Once enabled all the errors that match the rule will be marked as Business error.';
      break;
  }
  return statement;
};

export const getRowCalss = ({ rowData }) => (rowData.state === 2 ? '' : 'row-disabled');
